import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from './ui/icon'
import {
  mdiFolderCog,
  mdiFolderMove,
  mdiFolderPlusOutline,
  mdiMedal,
  mdiMedalOutline,
  mdiPlus,
  mdiUpload
} from '@mdi/js'
import { EmptyState } from './ui/empty-state'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { SECTION_ICONS } from '../lib/section-icons'
import { $api } from '../api/queryClient'
import { useApi } from '../api/hooks'
import {
  useUploadDocument,
  useContract,
  useContractDocumentFolders,
  useUpdateContract,
  refreshContractDocumentFolders,
} from '../hooks/useQueries'
import { AttachDocumentControl, type AttachDocumentControlLabels } from './documents/AttachDocumentControl'
import { DocumentPreviewCard } from './documents/DocumentPreviewCard'
import { DocumentFolderTree } from './documents/DocumentFolderTree'
import { DocumentMoveFolderDialog, type MovableDocument } from './documents/DocumentMoveFolderDialog'
import { ContractFolderLayoutDialog } from './documents/ContractFolderLayoutDialog'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import type { components } from '../api/schema'
import { collectDroppedFiles, filesFromFileList, appendOriginalPath, type DroppedFile } from '../lib/dropped-files'
import { ImportSelectField } from './imports/ImportSelect'

type Document = components['schemas']['Document']

/** Documents are grouped by their type, so the tree needs it on every row. */
type DocumentWithType = Document & { uid: string; document_type_uid?: string | null }

interface DocumentLink {
  uid: string
  entity_type: string
  entity_uid: string
  relation_type: string
}

interface Props {
  org: string
  contractUid: string
  flags?: string[] | null
  /** The contract's current main document (properties.source_document_uid), from the contract detail. */
  sourceDocumentUid?: string | null
}

/** Documents listed per page — the API caps `limit` at 500. */
const CONTRACT_DOCUMENTS_PAGE_SIZE = 500

/**
 * "Documents" section on the contract detail page.
 *
 * Since spec 2026-08-13-01 a document's relationship to a contract is the
 * first-class `documents.contract_id` column, not a `document_links` row: the
 * list filters on `contract_uid`, uploads send `contract_uid` (and no
 * `asset_uid`), and the flat card list became the same folder tree the asset
 * documents tab renders — `DocumentFolderTree` / `groupDocumentsByFolder` are
 * entity-agnostic, only the resolved layout differs.
 *
 * Legacy `attachment` links to *several* contracts were deliberately left in
 * place by the backfill (resolved open question 1), so attach/detach of those
 * links stays available here.
 *
 * The list query is gated on contract.flags including "document" (maintained
 * server-side from `contract_id`, see docs/database/flags-and-properties.md
 * §Contract flags) so most contracts — which have no documents — never issue
 * the request.
 */
export function ContractDocuments({ org, contractUid, flags, sourceDocumentUid }: Props) {
  const { t } = useTranslation('contracts')
  const { t: tFolders, i18n } = useTranslation('folders')
  const { request } = useApi()
  const queryClient = useQueryClient()

  const hasDocumentFlag = Array.isArray(flags) && flags.includes('document')

  const documentsQuery = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents',
    {
      params: {
        path: { orgCode: org },
        query: {
          contract_uid: contractUid,
          include: ['contract_uid', 'document_type_uid'],
          limit: CONTRACT_DOCUMENTS_PAGE_SIZE,
        },
      },
    },
    { enabled: hasDocumentFlag && !!contractUid },
  )

  const documents: DocumentWithType[] = useMemo(
    () => (((documentsQuery.data as any)?.data as DocumentWithType[]) || []),
    [documentsQuery.data],
  )

  // Folder structure resolved for this contract (own column → contract type →
  // ancestor type → org default). Fetched even when the contract has no
  // documents yet, so the layout picker is always reachable.
  const { data: folderTree } = useContractDocumentFolders(org, contractUid)
  const folders = useMemo(() => folderTree?.folders ?? [], [folderTree])
  const layoutUid = folderTree?.layout?.uid
  const layoutName = folderTree?.layout
    ? (folderTree.layout.name as Record<string, string> | undefined)?.[i18n.language]
      || (folderTree.layout.name as Record<string, string> | undefined)?.en
      || folderTree.layout.code
    : null

  // The contract's *own* layout column (not the resolved one), so the picker
  // opens on "inherit" when the structure comes from its type.
  const { data: contract } = useContract(org, contractUid)

  const classifiedTypeUids = useMemo(() => {
    const set = new Set<string>()
    for (const folder of folders) {
      for (const uid of folder.document_type_uids ?? []) set.add(uid)
    }
    return set
  }, [folders])

  const invalidateAfterChange = useCallback((documentUid?: string) => {
    // Filtered documents list for this contract.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as any[]
        if (key.length >= 2 && key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/documents') {
          const params = key.find((i) => i && typeof i === 'object' && 'params' in i)?.params
          return params?.path?.orgCode === org
            && (params?.query?.contract_uid === contractUid || params?.query?.linked_entity_uid === contractUid)
        }
        return false
      },
    })
    // Per-folder counts change with the document set.
    refreshContractDocumentFolders(queryClient, org, contractUid)
    // The detached/attached document's own links query.
    if (documentUid) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          if (key.length >= 2 && key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/documents/{documentUid}/links') {
            const params = key.find((i) => i && typeof i === 'object' && 'params' in i)?.params
            return params?.path?.orgCode === org && params?.path?.documentUid === documentUid
          }
          return false
        },
      })
    }
    // Contract detail query — flags may have changed, which gates this list.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as any[]
        if (key.length >= 2 && key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/contracts/{contractUid}') {
          const params = key.find((i) => i && typeof i === 'object' && 'params' in i)?.params
          return params?.path?.orgCode === org && params?.path?.contractUid === contractUid
        }
        return false
      },
    })
  }, [queryClient, org, contractUid])

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false)
  // A deposit can be a whole folder, so the dialog holds a LIST of files, each
  // remembering the path it had in the depositor's tree (spec 2026-08-16-04 §3).
  const [files, setFiles] = useState<DroppedFile[]>([])
  const [uploadError, setUploadError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Optional deposit session for this upload dialog (spec 2026-08-16-05 §2).
  const [selectedImportUid, setSelectedImportUid] = useState<string>('')
  const uploadMutation = useUploadDocument(org, selectedImportUid || undefined)

  // Folder-structure dialogs
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false)
  const [moveDocument, setMoveDocument] = useState<MovableDocument | null>(null)

  // Main-document designation (spec 2026-09-21 contract-main-source-document):
  // one attached document becomes the contract's source_document_uid — the one
  // the clause/extraction pipeline reads, which also unlocks the re-extract
  // action in the clauses tab.
  const updateContract = useUpdateContract(org, contractUid)
  const [sourceError, setSourceError] = useState('')
  const setSourceDocument = (doc: DocumentWithType, clear: boolean) => {
    setSourceError('')
    updateContract.mutate(
      { source_document_uid: clear ? null : doc.uid } as never,
      {
        onSuccess: () => invalidateAfterChange(doc.uid),
        onError: () => setSourceError(t('documents.set_source_error')),
      },
    )
  }

  const openUpload = () => {
    setFiles([])
    setUploadError('')
    setUploadOpen(true)
  }

  const closeUpload = () => {
    setUploadOpen(false)
    setFiles([])
    setUploadError('')
  }

  const handleFilesChosen = (chosen: DroppedFile[]) => {
    if (chosen.length > 0) setFiles(chosen)
    setUploadError('')
  }

  const handleUploadConfirm = async () => {
    if (files.length === 0) {
      setUploadError(t('documents.upload_error_no_file'))
      return
    }
    try {
      for (const { file, originalPath } of files) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', file.name)
        // The document's home is the contract — no asset_uid, so a tenant's
        // paperwork never shows up in a building's document views.
        formData.append('contract_uid', contractUid)
        appendOriginalPath(formData, originalPath)
        await uploadMutation.mutateAsync(formData)
      }
      invalidateAfterChange()
      closeUpload()
    } catch {
      setUploadError(t('documents.upload_error_generic'))
    }
  }

  // Attach-existing state (shared AttachDocumentControl — see spec
  // 2026-07-24-01-shared-attach-document-ui.md). Attaching an existing
  // document now *files* it on the contract (PATCH contract_uid) instead of
  // creating an attachment link.
  const [attachError, setAttachError] = useState('')
  const linkedUids = useMemo(() => new Set(documents.map((d) => d.uid).filter(Boolean)), [documents])
  const attachLabels: AttachDocumentControlLabels = useMemo(() => ({
    attachExisting: t('documents.attach_existing'),
    upload: t('documents.upload'),
    uploading: t('documents.upload'),
    pickDocumentTitle: t('documents.pick_document'),
    searchPlaceholder: t('documents.search_documents'),
    noDocumentsFound: t('documents.no_documents_found'),
    alreadyAttached: t('documents.already_attached'),
  }), [t])

  const attachExistingDocument = useCallback(async (documentUid: string) => {
    setAttachError('')
    try {
      await request(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}',
        'PATCH',
        {
          params: { path: { orgCode: org, documentUid } },
          body: { contract_uid: contractUid } as never,
        },
      )
      invalidateAfterChange(documentUid)
    } catch {
      setAttachError(t('documents.attach_existing_error_generic'))
    }
  }, [request, org, contractUid, invalidateAfterChange, t])

  // Detach state
  const [detachTarget, setDetachTarget] = useState<Document | null>(null)
  const [detaching, setDetaching] = useState(false)
  const [detachError, setDetachError] = useState('')

  const closeDetach = () => {
    setDetachTarget(null)
    setDetachError('')
  }

  const handleDetachConfirm = async () => {
    if (!detachTarget?.uid) return
    setDetaching(true)
    setDetachError('')
    try {
      // The document's home is the contract → clear contract_id. The API
      // refuses to leave a document with no home at all (422), which the
      // generic error below surfaces.
      if ((detachTarget as DocumentWithType).contract_uid === contractUid) {
        try {
          await request(
            '/api/v1/organizations/{orgCode}/documents/{documentUid}',
            'PATCH',
            {
              params: { path: { orgCode: org, documentUid: detachTarget.uid } },
              body: { contract_uid: null } as never,
            },
          )
        } catch (err: unknown) {
          // At-least-one-parent (spec 2026-08-13-01 §1): a document whose only
          // home is this contract cannot simply be unfiled — it would become
          // an orphan. Say so instead of failing generically.
          //
          // `useApi().request` rethrows the API error as
          // `new Error(JSON.stringify(payload))`, so the code has to be read
          // back out of the message.
          const message = err instanceof Error ? err.message : String(err)
          if (message.includes('DOCUMENT_WITHOUT_PARENT')) {
            setDetachError(t('documents.detach_error_last_home'))
            setDetaching(false)
            return
          }
          throw err
        }
        invalidateAfterChange(detachTarget.uid)
        closeDetach()
        return
      }

      // Legacy multi-contract attachment link (kept indefinitely by the
      // 2026-08-13-01 backfill) → drop the link instead.
      const linksResp = await request(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/links',
        'GET',
        { params: { path: { orgCode: org, documentUid: detachTarget.uid } } },
      )
      const links = ((linksResp as any)?.data || []) as DocumentLink[]
      const link = links.find(
        (l) => l.entity_type === 'contract' && l.entity_uid === contractUid && l.relation_type === 'attachment',
      )
      if (!link) {
        setDetachError(t('documents.detach_error_not_found'))
        setDetaching(false)
        return
      }
      await request(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/links/{linkUid}',
        'DELETE',
        { params: { path: { orgCode: org, documentUid: detachTarget.uid, linkUid: link.uid } } },
      )
      invalidateAfterChange(detachTarget.uid)
      closeDetach()
    } catch {
      setDetachError(t('documents.detach_error_generic'))
    } finally {
      setDetaching(false)
    }
  }

  // One document row, shared by the flat list and the folder tree (the tree
  // only supplies the indentation depth).
  const renderDocumentRow = (doc: DocumentWithType, depth: number) => {
    const typeUid = doc.document_type_uid
    // Typed but in no folder → the layout is missing a folder for that type;
    // offer the editor pre-filled with it (same affordance as the asset tab).
    const proposable = Boolean(typeUid) && !classifiedTypeUids.has(typeUid as string) && Boolean(layoutUid)
    return (
      <div
        key={doc.uid}
        className="flex items-start justify-between gap-2 py-1"
        style={depth > 0 ? { marginLeft: depth * 16 } : undefined}
        data-testid={`contract-document-row-${doc.uid}`}
      >
        <div className="min-w-0 flex-1">
          <DocumentPreviewCard
            org={org}
            documentUid={doc.uid}
            document={doc}
            testidPrefix={`contract-document-${doc.uid}`}
            onDetach={() => { setDetachTarget(doc); setDetachError('') }}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2 pt-2">
          {doc.uid === sourceDocumentUid && (
            <Badge
              variant="secondary"
              className="shrink-0"
              title={t('documents.main_document_hint')}
              data-testid="contract-doc-source-badge"
            >
              {t('documents.main_document')}
            </Badge>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setSourceDocument(doc, doc.uid === sourceDocumentUid)}
            aria-label={doc.uid === sourceDocumentUid ? t('documents.clear_main_document') : t('documents.set_as_main_document')}
            title={doc.uid === sourceDocumentUid ? t('documents.clear_main_document') : t('documents.set_as_main_document')}
            data-testid={doc.uid === sourceDocumentUid ? `contract-doc-clear-source-${doc.uid}` : `contract-doc-set-source-${doc.uid}`}
          >
            <AppIcon path={doc.uid === sourceDocumentUid ? mdiMedal : mdiMedalOutline} size="sm" />
          </Button>
          {proposable && layoutUid && (
            <Button
              asChild
              variant="outline"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={tFolders('contract_view.propose_folder')}
              title={tFolders('contract_view.propose_folder')}
              data-testid={`contract-document-propose-folder-${doc.uid}`}
            >
              <Link
                to="/$org/folder-layouts/$layoutUid"
                params={{ org, layoutUid }}
                search={{ document_type_uid: typeUid as string }}
              >
                <AppIcon path={mdiFolderPlusOutline} size="sm" />
              </Link>
            </Button>
          )}
          {folders.length > 0 && (
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => setMoveDocument({ uid: doc.uid, name: doc.name, document_type_uid: doc.document_type_uid })}
              aria-label={tFolders('contract_view.move_document')}
              title={tFolders('contract_view.move_document')}
              data-testid={`contract-document-move-${doc.uid}`}
            >
              <AppIcon path={mdiFolderMove} size="sm" />
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <Card className="mt-6" data-testid="contract-documents">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <AppIcon path={SECTION_ICONS.documents} size="sm" />
              {t('documents.title')}
            </CardTitle>
            {documents.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t(`documents.summary_${documents.length === 1 ? 'one' : 'other'}` as any, { count: documents.length })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AttachDocumentControl
              org={org}
              linkedUids={linkedUids}
              testidPrefix="contract-document"
              showUpload={false}
              labels={attachLabels}
              onUploaded={() => {}}
              onSelect={attachExistingDocument}
              className="flex flex-wrap"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={openUpload}
              aria-label={t('documents.upload')}
              title={t('documents.upload')}
              data-testid="contract-document-upload-btn"
            >
              <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t('documents.upload')}</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
      {/*
        * Folder-structure header row. Rendered in every state — tree, flat
        * list and empty — so a contract with no layout yet can still reach the
        * picker.
        */}
      {folderTree && (
        <div className="flex items-start justify-between gap-2" data-testid="contract-documents-layout-row">
          <p className="text-xs text-muted-foreground" data-testid="contract-documents-layout-name">
            {layoutName
              ? tFolders('contract_view.layout_label', { name: layoutName })
              : tFolders('contract_view.layout_none')}
            {folderTree.layout_source === 'contract_type' && (
              <span> {tFolders('contract_view.layout_from_type')}</span>
            )}
            {folderTree.layout_source === 'ancestor_contract_type' && (
              <span> {tFolders('contract_view.layout_from_parent_type')}</span>
            )}
            {folderTree.layout_source === 'default' && (
              <span> {tFolders('contract_view.layout_from_default')}</span>
            )}
          </p>
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={() => setLayoutDialogOpen(true)}
            aria-label={tFolders('contract_view.settings_button')}
            title={tFolders('contract_view.settings_button')}
            data-testid="contract-documents-layout-settings-btn"
          >
            <AppIcon path={mdiFolderCog} size="md" />
          </Button>
        </div>
      )}

      <ContractFolderLayoutDialog
        open={layoutDialogOpen}
        onOpenChange={setLayoutDialogOpen}
        org={org}
        contractUid={contractUid}
        currentLayoutUid={(contract as { document_folder_layout_uid?: string } | undefined)?.document_folder_layout_uid}
      />

      {/* Mounted per selected document so the update mutation is bound to
          that document's uid. */}
      {moveDocument && (
        <DocumentMoveFolderDialog
          open
          onOpenChange={open => { if (!open) setMoveDocument(null) }}
          org={org}
          contractUid={contractUid}
          folders={folders}
          document={moveDocument}
        />
      )}

      {attachError && (
        <p className="text-sm text-destructive" data-testid="contract-document-attach-error">
          {attachError}
        </p>
      )}

      {sourceError && (
        <p className="text-sm text-destructive" data-testid="contract-document-source-error">
          {sourceError}
        </p>
      )}

      {/*
        * The empty hint is shown alongside the folder tree, not instead of it:
        * a contract resolving a layout still has to say "no document yet"
        * rather than leave a set of zero-count folders speaking for itself.
        */}
      {(!hasDocumentFlag || documents.length === 0) && (
        <EmptyState
          variant="inline"
          icon={SECTION_ICONS.documents}
          title={t('documents.empty')}
          data-testid="contract-documents-empty"
        />
      )}

      {folders.length > 0 ? (
        <div data-testid="contract-documents-folder-tree">
          <DocumentFolderTree
            folders={folders}
            documents={documents}
            renderDocument={renderDocumentRow}
          />
        </div>
      ) : documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => renderDocumentRow(doc, 0))}
        </div>
      ) : null}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) closeUpload() }}>
        <DialogContent data-testid="contract-document-upload-dialog">
          <DialogHeader>
            <DialogTitle>{t('documents.upload_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={async (e) => {
                e.preventDefault()
                handleFilesChosen(await collectDroppedFiles(e.dataTransfer))
              }}
              onDragOver={(e) => e.preventDefault()}
              data-testid="contract-document-upload-dropzone"
            >
              {files.length > 0 ? (
                <div className="flex flex-col items-center gap-1 text-sm">
                  {files.slice(0, 5).map(({ file, originalPath }, i) => (
                    <div key={i} className="flex items-center justify-center gap-2">
                      <AppIcon tone="muted" path={mdiUpload} size="sm" />
                      <span className="text-foreground">{originalPath || file.name}</span>
                    </div>
                  ))}
                  {files.length > 5 && (
                    <span className="text-xs text-muted-foreground">+{files.length - 5}</span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  <AppIcon path={mdiUpload} size="lg" className="mx-auto mb-2" />
                  {t('documents.upload_drop')}
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={(e) => handleFilesChosen(filesFromFileList(e.target.files))}
              data-testid="contract-document-upload-input"
            />
            <ImportSelectField
              org={org}
              value={selectedImportUid}
              onChange={setSelectedImportUid}
              testidPrefix="contract-documents-import-select"
            />
            {uploadError && (
              <p className="text-sm text-destructive" data-testid="contract-document-upload-error">
                {uploadError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeUpload} data-testid="contract-document-upload-cancel">
              {t('documents.cancel')}
            </Button>
            <Button
              onClick={handleUploadConfirm}
              disabled={uploadMutation.isPending || files.length === 0}
              data-testid="contract-document-upload-confirm"
            >
              {t('documents.upload_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detach confirmation */}
      <AlertDialog open={!!detachTarget} onOpenChange={(o) => { if (!o) closeDetach() }}>
        <AlertDialogContent data-testid="contract-document-detach-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.detach_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.detach_confirm_body', { name: detachTarget?.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {detachError && <p className="text-sm text-destructive">{detachError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contract-document-detach-cancel">
              {t('documents.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDetachConfirm}
              disabled={detaching}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="contract-document-detach-confirm"
            >
              {t('documents.detach')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </CardContent>
    </Card>
  )
}
